import type { WidgetShape } from "@/app/ai/components/chat/widgetIntent";

// The skeleton's shape IS the widget shape — reuse the single source of truth
// (WidgetShape, which mirrors WIDGET_TYPES in ai/.../bi/_helpers.py) rather than
// maintaining a parallel union that must be kept in lockstep by hand.

interface ChartSkeletonProps {
  /** Optional caption shown under the shimmer (e.g. "Building your chart…"). */
  label?: string;
  /**
   * The widget shape being built, if known. Absent → the generic bar shimmer
   * (the historical behaviour), so a caller that can't derive it degrades
   * cleanly rather than guessing.
   */
  shape?: WidgetShape;
}

// Staggered heights so the shimmer reads as a forming bar chart. Each bar
// carries a stable id so React can reconcile by identity rather than position.
const BARS = [
  { id: "bar-1", height: 42 },
  { id: "bar-2", height: 68 },
  { id: "bar-3", height: 54 },
  { id: "bar-4", height: 84 },
  { id: "bar-5", height: 60 },
  { id: "bar-6", height: 74 },
  { id: "bar-7", height: 48 },
];

function BarsShimmer() {
  return (
    <div className="flex h-[180px] items-end gap-2 border-b border-l border-muted pb-px pl-2">
      {BARS.map((bar, i) => (
        <div
          key={bar.id}
          className="skeleton-sweep flex-1 rounded-t opacity-40"
          style={{ height: `${bar.height}%`, animationDelay: `${i * 120}ms` }}
        />
      ))}
    </div>
  );
}

// A rising polyline over a baseline — reads as "a trend is forming".
function LineShimmer() {
  return (
    <div className="relative h-[180px] border-b border-l border-muted">
      <svg
        className="h-full w-full animate-pulse [animation-duration:3.6s]"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <polyline
          points="0,78 20,60 40,66 60,40 80,30 100,14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          className="text-muted"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

function PieShimmer() {
  return (
    <div className="flex h-[180px] items-center justify-center">
      <div className="h-[150px] w-[150px] animate-pulse rounded-full border-[18px] border-muted [animation-duration:3.6s]" />
    </div>
  );
}

// One big greyed numeral — the KPI shape.
function ValueShimmer() {
  return (
    <div className="flex h-[180px] flex-col items-center justify-center gap-3">
      <div className="skeleton-sweep h-12 w-40 rounded" />
      <div className="skeleton-sweep h-3 w-24 rounded" />
    </div>
  );
}

const TABLE_ROWS = ["row-1", "row-2", "row-3", "row-4", "row-5"];

function TableShimmer() {
  return (
    <div className="flex h-[180px] flex-col gap-2">
      <div className="skeleton-sweep h-6 w-full rounded" />
      {TABLE_ROWS.map((id, i) => (
        <div
          key={id}
          className="skeleton-sweep h-4 w-full rounded"
          style={{ animationDelay: `${i * 90}ms` }}
        />
      ))}
    </div>
  );
}

function ShapeShimmer({ shape }: Readonly<{ shape?: WidgetShape }>) {
  switch (shape) {
    case "line_chart":
      return <LineShimmer />;
    case "pie_chart":
      return <PieShimmer />;
    case "single_value":
      return <ValueShimmer />;
    case "table":
      return <TableShimmer />;
    default:
      return <BarsShimmer />;
  }
}

/**
 * Chart-shaped loading placeholder. Shown while a statistics answer is being
 * computed so the user sees the *shape* of the result before the data arrives.
 * When `shape` is known (from the agent's tool call), it matches the real
 * widget type and `label` carries the real title; otherwise it falls back to
 * the generic bar shimmer — the historical behaviour.
 */
export function ChartSkeleton({ label, shape }: Readonly<ChartSkeletonProps>) {
  return (
    <div className="w-full" aria-hidden="true">
      <ShapeShimmer shape={shape} />
      {label && (
        <p className="mt-3 animate-pulse text-center text-xs text-muted-foreground">
          {label}
        </p>
      )}
    </div>
  );
}
